<script setup lang="ts">
import { SEGMENT_TRACK_EVENTS } from "~/constants";

export interface AppHeaderInvisibleLinksProps {
  links: IHeaderLink[];
}

const { t } = useI18n();
const route = useRoute();
const segment = useSegment();
const runtimeConfig = useRuntimeConfig().public;

const props = defineProps<AppHeaderInvisibleLinksProps>();
const gridRows = computed(() => `grid-template-rows: repeat(${Math.min(props.links.length, 12)}, minmax(0, 1fr))`);
const isLeague = runtimeConfig.APP_CUSTOMER.enable_leagues_view;
const popoverPlacement = computed(() => route.path.startsWith("/search") ? "bottom-end" : "bottom-start");

const processedLinks = computed(() =>
  props.links.map((link) => {
    const isExternal = link.href.includes("http");
    const displayTitle = link.id === "following" || link.id === "archive"
      ? t(`labels.${link.id}`)
      : link.title;

    return {
      ...link,
      isExternal,
      displayTitle,
      linkProps: isExternal
        ? { tag: "a" as const, href: link.href }
        : { tag: "nuxt-link" as const, to: link.href },
    };
  }),
);

function onHeaderLinkClick(link: IHeaderLink) {
  if (link.id === "archive")
    return;

  if (isLeague) {
    segment.track(SEGMENT_TRACK_EVENTS.CLICK_ON_LEAGUE, {
      league_id: link.id,
      league_name: link.title,
    });
  }
}
</script>

<template>
  <UMenu
    v-if="processedLinks.length"
    :dropdown="{
      placement: popoverPlacement,
    }"
  >
    <template #trigger="{ toggle }">
      <BaseButton
        name="header links"
        type="icon"
        title="Menu"
        class="transform rotate-90"
        @click="toggle"
      >
        <IconAction />
      </BaseButton>
    </template>
    <template #default="{ hide }">
      <UList class="grid grid-flow-col" :style="gridRows">
        <UListItem
          v-for="link in processedLinks"
          :key="link.id"
          class="max-w-[250px]"
          size="md"
          :has-hover="false"
        >
          <BaseButton
            link
            full-width
            type="secondary"
            v-bind="link.linkProps"
            :name="link.title"
            :title="link.title"
            class="w-full font-medium hover:!text-black hover:!no-underline"
            exact-active-class="!text-[var(--base-primary-color)]"
            @click.capture="() => {
              onHeaderLinkClick(link);
              hide();
            }"
          >
            <p class="line-clamp-1 text-base">
              {{ link.displayTitle }}
            </p>
          </BaseButton>
        </UListItem>
      </UList>
    </template>
  </UMenu>
</template>
